import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { action, observable } from 'mobx-angular';
import { lastValueFrom } from 'rxjs';

import { AdditionalCostCategoryModel } from '../../content/after-sales/model/additional-cost-category-model';
import { ErrorCostRateModel } from '../../content/after-sales/model/after-sales-error-cost-rate-model';
import { AfterSalesModel } from '../../content/after-sales/model/after-sales-model';
import { NotificationServiceWrapper } from '../notification-service-wrapper/notification-service-wrapper';
import { UserRights } from '../user-service/user-rights.enum';
import { UserService } from '../user-service/user.service';

import { AfterSalesService } from './after-sales.service';

@Injectable({
  providedIn: 'root',
})
export class AfterSalesStore {
  private readonly afterSalesService = inject(AfterSalesService);
  private readonly userService = inject(UserService);
  private readonly translateService = inject(TranslateService);
  private readonly notificationServiceWrapper = inject(NotificationServiceWrapper);

  private static readonly NOTIFICATION_ERROR_UNEXPECTED = 'notification_error_unexpected';
  private static readonly NOTIFICATION_UPDATE_SUCCESS = 'notification_update_success';

  @observable
  public afterSalesList: AfterSalesModel[];

  @observable
  public selectedAfterSalesDetail: AfterSalesModel;

  @observable /* Category which can be assigned to additional costs by selectBox*/
  public additionalCostsCategoryListSelectMenu: any[] = [];

  @observable
  public selectedAuditReportId: string;

  @observable /* Contains User-Rights*/ public userRights: UserRights[];

  @action /* Is executed with every initial call and stores the values in variable*/
  public async getAfterSalesTabData(): Promise<AfterSalesModel[]> {
    return await lastValueFrom(this.afterSalesService.getAfterSalesTabData());
  }

  @action /* Is executed when clicking on edit */ public async getAfterSalesDetails(
    auditReportId: string,
  ): Promise<boolean> {
    try {
      this.setSelectedAfterSalesDetails(
        await lastValueFrom(this.afterSalesService.getAfterSalesDetails(auditReportId)),
      );
      this.setSelectedAuditReportId(auditReportId);

      /* prepare the Additional Costs Category options for selection*/
      if (this.additionalCostsCategoryListSelectMenu.length === 0) {
        const categoryList: AdditionalCostCategoryModel = await lastValueFrom(
          this.afterSalesService.getAdditionalCostCategoryList(),
        );
        for (const index in categoryList) {
          this.additionalCostsCategoryListSelectMenu.push({
            key: categoryList[index].categoryId,
            value: this.translateService.instant(categoryList[index].categoryName),
          });
        }
      }

      return true;
    } catch {
      this.showNotification(AfterSalesStore.NOTIFICATION_ERROR_UNEXPECTED);
      return false;
    }
  }

  @action
  public setSelectedAuditReportId(selectedAuditReportId: string): void {
    this.selectedAuditReportId = selectedAuditReportId;
  }

  @action
  public setSelectedAfterSalesDetails(selectedAfterSalesDetail: AfterSalesModel): void {
    this.selectedAfterSalesDetail = selectedAfterSalesDetail;
  }

  @action
  public setAdditionalCostsCategoryList(additionalCostsCategoryListSelectMenu: any[]): void {
    this.additionalCostsCategoryListSelectMenu = additionalCostsCategoryListSelectMenu;
  }

  @action
  public getCurrentUserRights(): void {
    this.userRights = this.userService.getRights();
  }

  private showNotification(type: string): void {
    switch (type) {
      case AfterSalesStore.NOTIFICATION_UPDATE_SUCCESS: {
        this.notificationServiceWrapper.addSuccessNotification(
          this.translateService.instant('SAVED_TITLE'),
          this.translateService.instant('SAVED_SUCCESSFUL'),
        );
        break;
      }
      case AfterSalesStore.NOTIFICATION_ERROR_UNEXPECTED: {
        this.notificationServiceWrapper.addErrorNotification(
          this.translateService.instant('NOTIFICATION_UNEXPECTED_ERROR_HEADER'),
          this.translateService.instant('NOTIFICATION_UNEXPECTED_ERROR_MESSAGE'),
        );
        break;
      }
      // no default
    }
  }

  @action /* Is executed with every initial call and stores the values in variable*/
  public async getErrorCostRateTableData(): Promise<ErrorCostRateModel[]> {
    return await lastValueFrom(this.afterSalesService.getErrorCostRateTableData());
  }
}
